/**
 * GitHub SDK Client
 *
 * Provides access to GitHub repositories, pull requests, deployments,
 * comments, and check runs through the user's connected GitHub account.
 *
 * All operations are proxied through the Morph API - installation tokens
 * are never exposed to the client.
 */

import type {
  GitHubClientConfig,
  Installation,
  Repo,
  RepoConfig,
  PullRequest,
  PullRequestWithContext,
  Deployment,
  Comment,
  CheckRun,
  Project,
  ListReposInput,
  ListPullRequestsInput,
  GetPullRequestInput,
  ListDeploymentsInput,
  ListCommentsInput,
  CreateCommentInput,
  UpdateCommentInput,
  DeleteCommentInput,
  CreateCheckRunInput,
  UpdateCheckRunInput,
  UpdateInstallationInput,
  UpdateRepoInput,
  SyncReposInput,
  CreateProjectInput,
  UpdateProjectInput,
  ListProjectsInput,
} from "./types.js";

import { GitHubError, NoInstallationError, NotFoundError, PermissionError } from "./types.js";
import { MorphAPIClient } from "../../core/client.js";
import { APIResource } from "../../core/resource.js";

const DEFAULT_BASE_URL = "https://api.morphllm.com";
const DEFAULT_TIMEOUT = 30000;

/**
 * GitHub SDK Client
 *
 * @example
 * ```typescript
 * import { MorphClient } from '@morphllm/morphsdk';
 *
 * const morph = new MorphClient({ apiKey: process.env.MORPH_API_KEY });
 *
 * // List installations
 * const installations = await morph.github.installations.list();
 *
 * // Get PR with context
 * const pr = await morph.github.pullRequests.get({
 *   owner: "acme",
 *   repo: "app",
 *   number: 42
 * });
 * ```
 *
 * @deprecated Prefer the unified `MorphClient` (`new MorphClient({ apiKey }).github`).
 * Standalone clients remain only for backwards compatibility and may be removed in a future
 * major version — do not use them in new code.
 */
export class GitHubClient extends APIResource {
  private timeout: number;
  private debug: boolean;
  private defaultInstallationId?: string;

  /** Installation operations */
  public installations: {
    list: () => Promise<Installation[]>;
    get: (installationId: string) => Promise<Installation>;
    update: (input: UpdateInstallationInput) => Promise<Installation>;
    disconnect: (installationId: string) => Promise<{ success: boolean; alreadyDeleted: boolean }>;
  };

  /** Repository operations */
  public repos: {
    list: (input: ListReposInput) => Promise<RepoConfig[]>;
    get: (repoId: string) => Promise<RepoConfig>;
    update: (input: UpdateRepoInput) => Promise<RepoConfig>;
    remove: (repoId: string) => Promise<{ success: boolean; alreadyRemoved: boolean }>;
    sync: (input: SyncReposInput) => Promise<{ count: number }>;
  };

  /** Project operations (monorepo sub-projects) */
  public projects: {
    list: (input: ListProjectsInput) => Promise<Project[]>;
    create: (input: CreateProjectInput) => Promise<Project>;
    update: (input: UpdateProjectInput) => Promise<Project>;
    delete: (projectId: string) => Promise<{ success: boolean }>;
  };

  /** Pull request operations */
  public pullRequests: {
    list: (input: ListPullRequestsInput) => Promise<PullRequest[]>;
    get: (input: GetPullRequestInput) => Promise<PullRequestWithContext>;
  };

  /** Deployment operations */
  public deployments: {
    list: (input: ListDeploymentsInput) => Promise<Deployment[]>;
  };

  /** Comment operations */
  public comments: {
    list: (input: ListCommentsInput) => Promise<Comment[]>;
    create: (input: CreateCommentInput) => Promise<Comment>;
    update: (input: UpdateCommentInput) => Promise<Comment>;
    delete: (input: DeleteCommentInput) => Promise<void>;
  };

  /** Check run operations */
  public checkRuns: {
    create: (input: CreateCheckRunInput) => Promise<CheckRun>;
    update: (input: UpdateCheckRunInput) => Promise<CheckRun>;
  };

  /**
   * Get the GitHub App installation URL.
   * Users visit this URL once to install the Morph GitHub App on their account/org.
   */
  public getInstallUrl(): string {
    return "https://github.com/apps/morph-subagents/installations/new";
  }

  constructor(
    clientOrConfig: MorphAPIClient | GitHubClientConfig = {},
    options: { installationId?: string } = {},
  ) {
    const isClient = clientOrConfig instanceof MorphAPIClient;
    const config: GitHubClientConfig = isClient ? {} : clientOrConfig;
    super(
      isClient
        ? clientOrConfig
        : new MorphAPIClient({
            apiKey: config.apiKey,
            baseURL: config.baseUrl ?? DEFAULT_BASE_URL,
            timeout: config.timeout ?? DEFAULT_TIMEOUT,
            debug: config.debug,
          }),
    );
    this.timeout = config.timeout || DEFAULT_TIMEOUT;
    this.debug = config.debug || false;
    // When sharing a transport, installationId is passed via `options`.
    this.defaultInstallationId = isClient ? options.installationId : config.installationId;

    if (!this._client.resolveApiKey()) {
      throw new Error("API key required. Set MORPH_API_KEY or pass apiKey in config.");
    }

    // Bind all methods
    this.installations = {
      list: this.listInstallations.bind(this),
      get: this.getInstallation.bind(this),
      update: this.updateInstallation.bind(this),
      disconnect: this.disconnectInstallation.bind(this),
    };

    this.repos = {
      list: this.listRepos.bind(this),
      get: this.getRepo.bind(this),
      update: this.updateRepo.bind(this),
      remove: this.removeRepo.bind(this),
      sync: this.syncRepos.bind(this),
    };

    this.projects = {
      list: this.listProjects.bind(this),
      create: this.createProject.bind(this),
      update: this.updateProject.bind(this),
      delete: this.deleteProject.bind(this),
    };

    this.pullRequests = {
      list: this.listPullRequests.bind(this),
      get: this.getPullRequest.bind(this),
    };

    this.deployments = {
      list: this.listDeployments.bind(this),
    };

    this.comments = {
      list: this.listComments.bind(this),
      create: this.createComment.bind(this),
      update: this.updateComment.bind(this),
      delete: this.deleteComment.bind(this),
    };

    this.checkRuns = {
      create: this.createCheckRun.bind(this),
      update: this.updateCheckRun.bind(this),
    };
  }

  /**
   * Make an authenticated API request
   */
  private async request<T>(
    method: string,
    path: string,
    body?: Record<string, unknown>
  ): Promise<T> {
    if (this.debug) {
      console.log(`[GitHub SDK] ${method} ${path}`, body || "");
    }

    // Centralize transport (auth, retries, timeout, URL) but keep GitHub's own
    // error taxonomy by inspecting the raw response ourselves.
    let response: Response;
    try {
      response = await this._client.request<Response>(method, path, {
        body,
        timeout: this.timeout,
        raw: true,
      });
    } catch (error) {
      if (error instanceof Error && /timed out/i.test(error.message)) {
        throw new GitHubError("Request timeout", 408, "TIMEOUT");
      }
      throw new GitHubError(error instanceof Error ? error.message : "Unknown error", 500, "UNKNOWN");
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as Record<string, any>));
      const message = errorData.error || errorData.detail || errorData.message || response.statusText;

      if (this.debug) {
        console.error(`[GitHub SDK] Error ${response.status}:`, message);
      }

      // Map to specific error types
      if (response.status === 404) {
        if (message.includes("installation")) {
          throw new NoInstallationError(message);
        }
        throw new NotFoundError(message);
      }
      if (response.status === 403) {
        throw new PermissionError(message);
      }
      throw new GitHubError(message, response.status, errorData.code);
    }

    const data = await response.json();

    if (this.debug) {
      console.log(`[GitHub SDK] Response:`, JSON.stringify(data).slice(0, 200));
    }

    return data as T;
  }

  /**
   * Get the installation ID to use for a request
   */
  private getInstallationId(input?: { installationId?: string }): string | undefined {
    return input?.installationId || this.defaultInstallationId;
  }

  // ==========================================================================
  // Installations
  // ==========================================================================

  private async listInstallations(): Promise<Installation[]> {
    return this.request<Installation[]>("GET", "/api/v1/github/installations");
  }

  private async getInstallation(installationId: string): Promise<Installation> {
    return this.request<Installation>("GET", `/api/v1/github/installations/${installationId}`);
  }

  private async updateInstallation(input: UpdateInstallationInput): Promise<Installation> {
    if (this.debug) console.log("[GitHub SDK] updateInstallation", input.installationId);
    const { installationId, ...body } = input;
    return this.request<Installation>("PATCH", `/api/v1/github/installations/${installationId}`, body);
  }

  private async disconnectInstallation(installationId: string): Promise<{ success: boolean; alreadyDeleted: boolean }> {
    if (this.debug) console.log("[GitHub SDK] disconnectInstallation", installationId);
    return this.request<{ success: boolean; alreadyDeleted: boolean }>("DELETE", `/api/v1/github/installations/${installationId}`);
  }

  // ==========================================================================
  // Repositories
  // ==========================================================================

  private async listRepos(input: ListReposInput): Promise<RepoConfig[]> {
    const installationId = input.installationId || this.defaultInstallationId;
    if (!installationId) {
      throw new NoInstallationError("installationId required. Call installations.list() first.");
    }
    return this.request<RepoConfig[]>("GET", `/api/v1/github/installations/${installationId}/repos`);
  }

  private async getRepo(repoId: string): Promise<RepoConfig> {
    if (this.debug) console.log("[GitHub SDK] getRepo", repoId);
    return this.request<RepoConfig>("GET", `/api/v1/github/repos/${repoId}`);
  }

  private async updateRepo(input: UpdateRepoInput): Promise<RepoConfig> {
    if (this.debug) console.log("[GitHub SDK] updateRepo", input.repoId);
    const { repoId, ...body } = input;
    return this.request<RepoConfig>("PATCH", `/api/v1/github/repos/${repoId}`, body);
  }

  private async removeRepo(repoId: string): Promise<{ success: boolean; alreadyRemoved: boolean }> {
    if (this.debug) console.log("[GitHub SDK] removeRepo", repoId);
    return this.request<{ success: boolean; alreadyRemoved: boolean }>("DELETE", `/api/v1/github/repos/${repoId}`);
  }

  private async syncRepos(input: SyncReposInput): Promise<{ count: number }> {
    const installationId = input.installationId || this.defaultInstallationId;
    if (!installationId) {
      throw new NoInstallationError("installationId required. Call installations.list() first.");
    }
    if (this.debug) console.log("[GitHub SDK] syncRepos", installationId);
    return this.request<{ count: number }>("POST", `/api/v1/github/installations/${installationId}/repos/sync`);
  }

  // ==========================================================================
  // Projects (monorepo sub-projects)
  // ==========================================================================

  private async listProjects(input: ListProjectsInput): Promise<Project[]> {
    if (this.debug) console.log("[GitHub SDK] listProjects", input.repoId);
    return this.request<Project[]>("GET", `/api/v1/github/repos/${input.repoId}/projects`);
  }

  private async createProject(input: CreateProjectInput): Promise<Project> {
    if (this.debug) console.log("[GitHub SDK] createProject", input.repoId, input.name);
    const { repoId, ...body } = input;
    return this.request<Project>("POST", `/api/v1/github/repos/${repoId}/projects`, body);
  }

  private async updateProject(input: UpdateProjectInput): Promise<Project> {
    if (this.debug) console.log("[GitHub SDK] updateProject", input.projectId);
    const { projectId, ...body } = input;
    return this.request<Project>("PATCH", `/api/v1/github/projects/${projectId}`, body);
  }

  private async deleteProject(projectId: string): Promise<{ success: boolean }> {
    if (this.debug) console.log("[GitHub SDK] deleteProject", projectId);
    return this.request<{ success: boolean }>("DELETE", `/api/v1/github/projects/${projectId}`);
  }

  // ==========================================================================
  // Pull Requests
  // ==========================================================================

  private async listPullRequests(input: ListPullRequestsInput): Promise<PullRequest[]> {
    const installationId = this.getInstallationId(input);
    const params = new URLSearchParams({
      owner: input.owner,
      repo: input.repo,
      ...(input.state && { state: input.state }),
      ...(installationId && { installationId }),
    });
    return this.request<PullRequest[]>("GET", `/api/v1/github/pulls?${params}`);
  }

  private async getPullRequest(input: GetPullRequestInput): Promise<PullRequestWithContext> {
    const installationId = this.getInstallationId(input);
    const params = new URLSearchParams({
      owner: input.owner,
      repo: input.repo,
      ...(installationId && { installationId }),
    });
    return this.request<PullRequestWithContext>(
      "GET",
      `/api/v1/github/pulls/${input.number}?${params}`
    );
  }

  // ==========================================================================
  // Deployments
  // ==========================================================================

  private async listDeployments(input: ListDeploymentsInput): Promise<Deployment[]> {
    const installationId = this.getInstallationId(input);
    const params = new URLSearchParams({
      owner: input.owner,
      repo: input.repo,
      ...(input.sha && { sha: input.sha }),
      ...(input.environment && { environment: input.environment }),
      ...(installationId && { installationId }),
    });
    return this.request<Deployment[]>("GET", `/api/v1/github/deployments?${params}`);
  }

  // ==========================================================================
  // Comments
  // ==========================================================================

  private async listComments(input: ListCommentsInput): Promise<Comment[]> {
    const installationId = this.getInstallationId(input);
    const params = new URLSearchParams({
      owner: input.owner,
      repo: input.repo,
      pr: String(input.pr),
      ...(installationId && { installationId }),
    });
    return this.request<Comment[]>("GET", `/api/v1/github/comments?${params}`);
  }

  private async createComment(input: CreateCommentInput): Promise<Comment> {
    const installationId = this.getInstallationId(input);
    return this.request<Comment>("POST", "/api/v1/github/comments", {
      owner: input.owner,
      repo: input.repo,
      pr: input.pr,
      body: input.body,
      ...(installationId && { installationId }),
    });
  }

  private async updateComment(input: UpdateCommentInput): Promise<Comment> {
    const installationId = this.getInstallationId(input);
    return this.request<Comment>("PATCH", `/api/v1/github/comments/${input.commentId}`, {
      owner: input.owner,
      repo: input.repo,
      body: input.body,
      ...(installationId && { installationId }),
    });
  }

  private async deleteComment(input: DeleteCommentInput): Promise<void> {
    const installationId = this.getInstallationId(input);
    const params = new URLSearchParams({
      owner: input.owner,
      repo: input.repo,
      ...(installationId && { installationId }),
    });
    await this.request<void>("DELETE", `/api/v1/github/comments/${input.commentId}?${params}`);
  }

  // ==========================================================================
  // Check Runs
  // ==========================================================================

  private async createCheckRun(input: CreateCheckRunInput): Promise<CheckRun> {
    const installationId = this.getInstallationId(input);
    return this.request<CheckRun>("POST", "/api/v1/github/check-runs", {
      owner: input.owner,
      repo: input.repo,
      sha: input.sha,
      name: input.name,
      status: input.status,
      ...(input.title && { title: input.title }),
      ...(input.summary && { summary: input.summary }),
      ...(installationId && { installationId }),
    });
  }

  private async updateCheckRun(input: UpdateCheckRunInput): Promise<CheckRun> {
    const installationId = this.getInstallationId(input);
    return this.request<CheckRun>("PATCH", `/api/v1/github/check-runs/${input.checkRunId}`, {
      owner: input.owner,
      repo: input.repo,
      ...(input.status && { status: input.status }),
      ...(input.conclusion && { conclusion: input.conclusion }),
      ...(input.title && { title: input.title }),
      ...(input.summary && { summary: input.summary }),
      ...(input.text && { text: input.text }),
      ...(installationId && { installationId }),
    });
  }
}
